import React, { useEffect, useState } from 'react';
import {
	useGetOneEvent,
	useGetUsersToCheckOffForEvent,
	useToggleParticipationStatusForRelatedUserGroup,
	useUpdateConfirmedAttending,
	useUpdateParticipationStatus,
	useUpdateTodoCompletedStatus,
	isUserAllowedToHandleEvent,
	useGetParticipationReportCustomFieldValues,
} from '../../../FrontEndReact/src/Core/Services/event';
import { formatDate, formatToSimpleTimeString } from '../../../FrontEndReact/src/Core/Utils/dateUtils';
import { BizCheckBox } from '../../../FrontEndReact/src/Core/Components/Mini/BizCheckBox';
import { EventStatus, ParticipationStatus } from '../../../FrontEndReact/src/Core/Types/event';
import { useGetRelatedUsers, useGetUserSession } from '../../../FrontEndReact/src/Core/Hooks/useUser';
import { Event } from '../../../FrontEndReact/src/Core/Types/event';
import { User } from '../../../FrontEndReact/src/Core/Types/user';
import { BizButton } from '../../../FrontEndReact/src/Core/Components/Mini/BizButton';
import './OneEvent.scss';
import { useGetTranslations } from '../../../Js/React/Services/legacy';
import { BizSwitch } from '../../../FrontEndReact/src/Core/Components/Mini/BizSwitch';
import BizInput from '../../../FrontEndReact/src/Core/Components/Mini/BizInput';
import { GenericCustomField, GenericCustomFieldDataType } from '../../../FrontEndReact/src/Core/Types/genericCustomField';
import { getBackgroundImageStyle } from '../../../FrontEndReact/src/Core/Services/mediabank';
import { getPathWithRootPath } from '../../../FrontEndReact/src/Core/Services/common';

type EventParticipationWUserRelationsProps = {
	evnt: Event;
	users: User[];
};

const translationKeys = ['GLOBAL.SAVE', 'EVENT.CONTACT.HEADER'];

const EventParticipationWUserRelations = ({ users, evnt }: EventParticipationWUserRelationsProps) => {
	const { toggleParticipationStatusForRelatedUserGroup, statusState } = useToggleParticipationStatusForRelatedUserGroup();
	const [checkedUserIds, setCheckedUserIds] = useState<number[]>(evnt.RelatedParticipationUserIds ?? []);
	const loggedInUserSession = useGetUserSession();
	const translations = useGetTranslations(translationKeys);

	useEffect(() => {
		setCheckedUserIds(evnt.RelatedParticipationUserIds ?? []);
	}, [evnt.RelatedParticipationUserIds]);

	if (!loggedInUserSession) return;

	const handleUserCheck = (userId: number, checked: boolean) => {
		if (checked) {
			setCheckedUserIds([...checkedUserIds, userId]);
		} else {
			setCheckedUserIds(checkedUserIds.filter((id) => id !== userId));
		}
	};

	const toggleAllHandler = (checked: boolean) => {
		if (checked) {
			setCheckedUserIds([...users.map((u) => u.Id), loggedInUserSession.UserId]);
		} else {
			setCheckedUserIds([]);
		}
	};

	const handleSaveClick = () => {
		toggleParticipationStatusForRelatedUserGroup(evnt.Id, checkedUserIds);
	};

	const checkedCount = checkedUserIds.length;
	const allChecked = checkedCount === users.length + 1;
	const rsvpPassed = new Date(evnt.RSVPDateTimeUtc) < new Date();
	const isCancelled = evnt.Status === EventStatus.Cancelled;

	return (
		<div className='event-participation-user-relations'>
			<div className='user-relation-summary'>
				<span className='checked-count'>{checkedCount}</span>
				<div>
					{rsvpPassed && <span className='rsvp-passed-text'>Anmälningstiden har gått ut</span>}
					{!rsvpPassed && (
						<>
							{' '}
							<span>Anmäl alla</span>
							<BizSwitch
								checked={allChecked}
								onChange={(checked) => toggleAllHandler(checked)}
								disabled={rsvpPassed}
							/>
						</>
					)}
				</div>
			</div>
			<ul className='user-relation-list'>
				<li>
					<span className='name'>{loggedInUserSession.User.FullName}</span>
					<BizSwitch
						checked={checkedUserIds.indexOf(loggedInUserSession.UserId) >= 0}
						onChange={(checked) => handleUserCheck(loggedInUserSession.UserId, checked)}
						disabled={rsvpPassed}
					/>
				</li>
				{users.map((user) => (
					<li key={user.Id}>
						<span className='name'>{user.FullName}</span>
						<BizSwitch
							checked={checkedUserIds.indexOf(user.Id) >= 0}
							onChange={(checked) => handleUserCheck(user.Id, checked)}
							disabled={rsvpPassed}
						/>
					</li>
				))}
			</ul>
			<div className='button-wrapper'>
				<BizButton
					text={translations['GLOBAL.SAVE'] ?? ''}
					onClick={handleSaveClick}
					state={statusState}
					legacy={true}
					className='biz-btn biz-primary'
					disabled={rsvpPassed || isCancelled}
				></BizButton>
			</div>
		</div>
	);
};

type EventParticipationProps = {
	evnt: Event;
};

const EventParticipation = ({ evnt }: EventParticipationProps) => {
	const { updateParticipationStatus } = useUpdateParticipationStatus();
	const relatedUsers = useGetRelatedUsers();

	if (relatedUsers !== undefined && relatedUsers.length > 0) {
		return <EventParticipationWUserRelations evnt={evnt} users={relatedUsers} />;
	}

	return (
		<div className='event-participation'>
			{(evnt.UserStatus === ParticipationStatus.None || evnt.UserStatus === ParticipationStatus.Invited) && (
				<div>
					<span onClick={() => updateParticipationStatus(evnt.Id, ParticipationStatus.Attending)} className='register'>
						Register
					</span>
					<span
						onClick={() => updateParticipationStatus(evnt.Id, ParticipationStatus.NotAttending)}
						className='unregister'
					>
						I will not attend
					</span>
				</div>
			)}
			{evnt.UserStatus === ParticipationStatus.Attending && (
				<div>
					<span className='register'>Status: Attending</span>
					<span
						onClick={() => updateParticipationStatus(evnt.Id, ParticipationStatus.NotAttending)}
						className='unregister'
					>
						Not attending
					</span>
				</div>
			)}
			{evnt.UserStatus === ParticipationStatus.NotAttending && (
				<div>
					<span className='unregister'>Status: Not attending</span>
					<span onClick={() => updateParticipationStatus(evnt.Id, ParticipationStatus.Attending)} className='register'>
						Attending
					</span>
				</div>
			)}
		</div>
	);
};

const EventParticipationLabel = ({ evnt }: EventParticipationProps) => {
	const relatedUsers = useGetRelatedUsers();

	if (
		(evnt.UserStatus === ParticipationStatus.Attending ||
			(relatedUsers !== undefined &&
				relatedUsers.length > 0 &&
				evnt.RelatedParticipationUserIds &&
				evnt.RelatedParticipationUserIds.length > 0)) === false
	)
		return null;

	return (
		<div>
			<i className='fa-regular	 fa-circle-check'></i> Du är anmäld till aktiviteten
		</div>
	);
};

type EventParticipationReportProps = {
	evnt: Event;
};

const EventParticipationReport = ({ evnt }: EventParticipationReportProps) => {
	const loggedInUserSession = useGetUserSession();
	const possibleParticipants = useGetUsersToCheckOffForEvent(evnt.Id);
	const customFields = useGetParticipationReportCustomFieldValues(evnt.Id);
	const { updateConfirmedAttending, statusState } = useUpdateConfirmedAttending();
	const [checkedUserIds, setCheckedUserIds] = useState<number[]>([]);
	const [internalComment, setInternalComment] = useState<string>(evnt.InternalComment ?? '');
	const [customFieldValues, setCustomFieldValues] = useState<{ [key: number]: string }>({});
	/*const translations = useGetTranslations(translationKeys);*/

	useEffect(() => {
		if (possibleParticipants) {
			setCheckedUserIds(
				possibleParticipants
					.filter(
						(pp) => pp.Status === ParticipationStatus.Attending || pp.Status === ParticipationStatus.ConfirmedAttended
					)
					.map((pp) => pp.ParticipantUserId)
			);
		}
	}, [possibleParticipants]);

	useEffect(() => {
		if (customFields) {
			const initialCustomFieldValues: { [key: number]: string } = {};
			customFields.forEach((cf) => {
				initialCustomFieldValues[cf.Id] = cf.Value?.Value ?? '';
			});
			setCustomFieldValues(initialCustomFieldValues);
		}
	}, [customFields]);

	if (!loggedInUserSession || isUserAllowedToHandleEvent(evnt, loggedInUserSession.User) === false || !possibleParticipants)
		return null;

	const updateCustomFieldValue = (customFieldId: number, value: string) => {
		setCustomFieldValues({ ...customFieldValues, [customFieldId]: value });
	};

	const getUpdatedCustomFieldValues = (): GenericCustomField[] | null => {
		if (!customFields) return null;

		return customFields.map((cf) => {
			return {
				...cf,
				Value: {
					...cf.Value,
					Value: customFieldValues[cf.Id],
					GenericCustomFieldId: cf.Value?.GenericCustomFieldId ?? 0,
					ObjectId: evnt.Id,
				},
			};
		});
	};

	const handleUserCheck = (userId: number, checked: boolean) => {
		if (checked) {
			setCheckedUserIds([...checkedUserIds, userId]);
		} else {
			setCheckedUserIds(checkedUserIds.filter((id) => id !== userId));
		}
	};

	const handleCheckAll = (checked: boolean) => {
		if (checked) {
			setCheckedUserIds(possibleParticipants.map((pp) => pp.ParticipantUserId));
		} else {
			setCheckedUserIds([]);
		}
	};

	const sortedParticipants = possibleParticipants.sort((a, b) => {
		// Group by MainUserName first
		const nameA = a.MainUserName ?? '';
		const nameB = b.MainUserName ?? '';
		const nameCompare = nameA.localeCompare(nameB);
		if (nameCompare !== 0) return nameCompare;

		// If names are the same, use MainUserId to avoid mixing same-name groups
		const groupCompare = (a.MainUserId ?? 0) - (b.MainUserId ?? 0);
		if (groupCompare !== 0) return groupCompare;

		// Now we're inside the same group

		// Main user first
		const aIsMain = a.MainUserId === a.ParticipantUserId;
		const bIsMain = b.MainUserId === b.ParticipantUserId;
		if (aIsMain && !bIsMain) return -1;
		if (!aIsMain && bIsMain) return 1;

		// Sort remaining members by ParticipantName
		return a.ParticipantName.localeCompare(b.ParticipantName);
	});

	let previousMainUserId;

	return (
		<div className='event-participation-toggler'>
			{!!customFields && (
				<div className='event-participation-customfields-wrapper'>
					{customFields.map((cf) => (
						<div key={cf.Id} className='event-participation-customfield'>
							<span>{cf.Name}</span>
							<BizInput
								type={cf.DataType === GenericCustomFieldDataType.TextboxNumber ? 'number' : 'text'}
								value={customFieldValues[cf.Id]}
								onChange={(e) => updateCustomFieldValue(cf.Id, e.target.value)}
							/>
						</div>
					))}
				</div>
			)}
			<div className='event-participation-toggler-header'>
				<span>Närvarande deltagare</span>
				<span>
					{checkedUserIds.length}/{possibleParticipants.length}
				</span>
				<div>
					<BizSwitch
						checked={checkedUserIds.length === sortedParticipants.length}
						onChange={(checked) => handleCheckAll(checked)}
					/>
				</div>
			</div>
			{sortedParticipants.map((pp) => {
				const isNewGroup = pp.MainUserId !== previousMainUserId;
				previousMainUserId = pp.MainUserId;

				return (
					<div
						key={pp.ParticipantUserId}
						className={`event-possible-participant${isNewGroup ? ' event-possible-participant-divider' : ''}`}
					>
						<div className={`main-participant`}>
							<div>
								<span className='participant-name'>
									{pp.ParticipantName}{' '}
									{pp.MainUserRelationType !== null && <span>({pp.MainUserRelationType.Name})</span>}
								</span>
								{pp.UserComment && <span className='participant-comment'>{pp.UserComment}</span>}
							</div>
							<BizSwitch
								checked={checkedUserIds.indexOf(pp.ParticipantUserId) >= 0}
								onChange={(checked) => handleUserCheck(pp.ParticipantUserId, checked)}
							/>
						</div>
					</div>
				);
			})}
			<div className='event-internal-comment'>
				<div className='comment-text-header'>Dina tankar och kommentarer (fritext)</div>
				<div className='comment-text'>
					<textarea value={internalComment} onChange={(e) => setInternalComment(e.target.value)}></textarea>
				</div>
			</div>
			<div className='event-participation-toggler-footer'>
				{/*translations['GLOBAL.SAVE'] ?? */}
				<BizButton
					text={'Spara närvaro och skicka in'}
					state={statusState}
					onClick={() =>
						updateConfirmedAttending(evnt.Id, checkedUserIds, internalComment, true, getUpdatedCustomFieldValues())
					}
					className='biz-btn biz-primary'
					disabled={evnt.StartDateTimeUtc > new Date()}
					legacy={true}
				></BizButton>
				{evnt.StartDateTimeUtc > new Date() && (
					<span className='not-started-warning'>
						<span>Det går inte att skicka in före aktiviteten har startat.</span>
					</span>
				)}
			</div>
		</div>
	);
};

type OneEventProps = {
	eventId: number;
	evnt?: Event;
};

const OneEvent = ({ eventId, evnt: pEvent }: OneEventProps) => {
	const fEvent = useGetOneEvent(eventId);
	const { updateTodoCompletedStatus } = useUpdateTodoCompletedStatus();
	const loggedInUserSession = useGetUserSession();
	const [showParticipationReport, setShowParticipationReport] = useState(false);

	const evnt = pEvent ?? fEvent;

	console.log('evnt', evnt);

	if (!evnt) return null;

	const rsvpPassed = new Date(evnt.RSVPDateTimeUtc) < new Date();
	console.log('rsvp passed', rsvpPassed);

	const allowedToHandlerEvent =
		loggedInUserSession !== undefined && loggedInUserSession !== null
			? isUserAllowedToHandleEvent(evnt, loggedInUserSession.User)
			: false;
	console.log('allowedToHandlerEvent', allowedToHandlerEvent, loggedInUserSession?.User);

	const toggleTodo = (todoId: number, checked: boolean) => {
		updateTodoCompletedStatus(evnt.Id, todoId, checked);
	};

	const formatDescription = (description: string) => {
		return description?.replaceAll('\n', '<br>');
	};

	return (
		<div className={`one-event-wrapper ${rsvpPassed ? 'rsvp-passed' : ''}`}>
			<div className='event-top' style={getBackgroundImageStyle(evnt.MbfObject, evnt.ImageUrl)}>
				<span className='event-name'>{evnt.Name}</span>
				{allowedToHandlerEvent && (
					<span
						className='participation-report-btn'
						onClick={() => setShowParticipationReport(!showParticipationReport)}
					>
						Närvaro <i className='far fa-pen'></i>
					</span>
				)}
			</div>
			<div className='event-info'>
				<div className='event-location'>
					<span className='event-info-item'>
						<i className='fa-regular fa-location-dot'></i>
						<span>{evnt.Location}</span>
					</span>
				</div>
				<div className='event-date-time'>
					<span className='event-info-item'>
						<i className='fa-light fa-calendar-days'></i>
						<span>{formatDate(evnt.StartDateTimeUtc, 'dd MMMM')}</span>
					</span>
					<span className='event-info-item'>
						<i className='fa-light fa-clock'></i>
						<span>
							{formatToSimpleTimeString(evnt.StartDateTimeUtc)} - {formatToSimpleTimeString(evnt.EndDateTimeUtc)}
						</span>
					</span>
				</div>
				<div className='event-participation-label'>
					<EventParticipationLabel evnt={evnt} />
				</div>
				{evnt.Status === EventStatus.Cancelled && (
					<div className='event-cancelled'>
						<i className='fa-regular fa-circle-xmark'></i> <span>Inställt!</span>
					</div>
				)}
				{allowedToHandlerEvent && evnt.Status === EventStatus.Upcoming && evnt.StartDateTimeUtc < new Date() && (
					<div className='event-not-completed'>
						<i className='fa-solid fa-triangle-exclamation'></i> <span>Ej inskickat!</span>
					</div>
				)}
			</div>
			{showParticipationReport && <EventParticipationReport evnt={evnt} />}
			{showParticipationReport === false && (
				<>
					{/*customer specific: ShortDescription, only visible for event admins*/}
					<p
						className='event-description'
						dangerouslySetInnerHTML={{
							__html: formatDescription(!allowedToHandlerEvent ? evnt.Description : evnt.ShortDescription),
						}}
					></p>
					<EventContactCard evnt={evnt} />
					{evnt.CanParticipate && evnt.DoNotRequireRSVP === true && (
						<div className='event-no-rsvp-required'>Ingen anmälan behövs</div>
					)}
					{evnt.CanParticipate && <EventParticipation evnt={evnt} />}
					{evnt.Todos !== null && (
						<div className='event-todos'>
							<ul>
								{evnt.Todos.map((todo) => (
									<li key={todo.Id} className={todo.IsCompleted ? 'checked' : ''}>
										<div>
											<span className='event-todo-text'>{todo.Text}</span>
											<span className='event-todo-desc'>{todo.Description}</span>
										</div>
										<div>
											<BizCheckBox
												value={todo.IsCompleted}
												onChange={(checked) => toggleTodo(todo.Id, checked)}
											/>
										</div>
									</li>
								))}
							</ul>
						</div>
					)}
					{evnt.HtmlTexts !== null &&
						evnt.HtmlTexts.map((ht) => (
							<div key={ht.Id} className='event-html-text'>
								<div dangerouslySetInnerHTML={{ __html: ht.TextDesktop }}></div>
							</div>
						))}
				</>
			)}
		</div>
	);
};

export default OneEvent;

type EventContactCardProps = {
	evnt?: Event;
};

function EventContactCard({ evnt }: EventContactCardProps) {
	const translations = useGetTranslations(translationKeys);
	if (!evnt) return null;

	const name = evnt.OrganizerName;
	const email = evnt.OrganizerEmail;
	const phone = evnt.OrganizerPhone;

	//return null if all values are empty
	if (!name && !email && !phone) return null;

	return (
		<div className='event-contact-card-wrapper'>
			<span className='event-contant-card-header'>{translations['EVENT.CONTACT.HEADER'] ?? ''}</span>
			<div className='event-contact-card'>
				{!!evnt.OrganizerPhotoUrl && (
					<div className='contact-photo'>
						<img src={getPathWithRootPath(evnt.OrganizerPhotoUrl ?? '')} alt='Contact' />
					</div>
				)}
				<div className='contact-info'>
					<div className='contact-info-top'>
						{!!name && (
							<span className='contact-name'>
								{name}
								{!!phone && <span>. </span>}
							</span>
						)}
						{!!phone && <span className='contact-phone'>Telefon {phone}</span>}
					</div>
					{!!email && (
						<div className='contact-info-bottom'>
							{!!email && (
								<a href={`mailto:${email}`} className='contact-email'>
									{email}
								</a>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
